import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Landing page after Stripe Checkout for a Legal Translation payment.
 * The webhook flips the request to `submitted_to_vendor` asynchronously, so
 * we poll the user's translation list until the matching session_id leaves
 * `pending_payment`. Then redirect to /dashboard/legal-translations.
 */
export default function TranslationPaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('activating'); // activating | ready | timeout | unauthenticated
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    if (!token) { setStatus('unauthenticated'); return undefined; }

    let cancelled = false;
    const maxAttempts = 20; // ~60s
    let attempt = 0;

    const userJson = JSON.parse(localStorage.getItem('user') || '{}');
    const firmJson = JSON.parse(localStorage.getItem('firm') || '{}');
    const firmId = userJson?.firm?.id || userJson?.firm_id || firmJson?.id;

    const tick = async () => {
      if (cancelled) return;
      attempt += 1;
      if (!firmId) {
        setStatus('ready'); // can't poll, just show success and let user click through
        return;
      }
      try {
        const res = await fetch(`${API}/translations/${firmId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const items = await res.json();
          const match = (items || []).find(
            (r) => r.stripe_checkout_session_id === sessionId || r.stripe_session_id === sessionId
          );
          if (match && match.status !== 'pending_payment') {
            setStatus('ready');
            return;
          }
        }
      } catch (_e) { /* swallow */ }
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }
      setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [token, sessionId]);

  const goToDashboard = () => navigate('/dashboard/legal-translations?payment=success');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-violet-950 to-indigo-950 flex flex-col items-center justify-center px-4 py-10" data-testid="translation-payment-success">
      <BrandLogo className="mb-8" />
      <div className="w-full max-w-md bg-white/5 backdrop-blur-xl rounded-3xl border border-violet-500/20 p-8 shadow-2xl shadow-violet-900/40 text-center">
        {status === 'activating' && (
          <>
            <Loader2 className="w-12 h-12 text-violet-300 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Confirming your payment…</h1>
            <p className="text-sm text-slate-300 mt-2">We're submitting your request to the translation team.</p>
          </>
        )}
        {status === 'ready' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Payment confirmed!</h1>
            <p className="text-sm text-slate-300 mt-2">Your translation request has been submitted to our vendor team. You'll be notified by email when it's ready.</p>
            <button
              onClick={goToDashboard}
              className="mt-6 w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-lg hover:shadow-violet-600/40 transition-all"
              data-testid="translation-payment-success-cta"
            >
              View My Translations
            </button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Payment received</h1>
            <p className="text-sm text-slate-300 mt-2">Stripe confirmed your payment. The status update is taking a little longer than usual - your request will appear in your dashboard shortly.</p>
            <button
              onClick={goToDashboard}
              className="mt-6 w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-lg hover:shadow-violet-600/40 transition-all"
              data-testid="translation-payment-success-cta"
            >
              Go to dashboard
            </button>
          </>
        )}
        {status === 'unauthenticated' && (
          <>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Please log in</h1>
            <p className="text-sm text-slate-300 mt-2">Your payment was successful. Sign in to see your translation request status.</p>
            <Link
              to="/auth"
              className="mt-6 inline-block w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-lg hover:shadow-violet-600/40 transition-all"
            >
              Sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
